import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Iter 33c — Compact "Contact path / Evidence source" affordance.
 *
 * Renders a tiny external-link icon + hostname inline next to a
 * company / target name. Honours the operator-set UI rules:
 *   • If `target_url` is present → labelled "Contact path".
 *   • Else if `best_evidence_url` is present → labelled "Evidence source".
 *   • Otherwise renders nothing (component returns null).
 *   • Always opens in a new tab (`target="_blank"`, `rel="noopener noreferrer"`).
 *   • Visible label is hostname only (e.g. `pitchero.com`,
 *     `fulltime.thefa.com`) — never the raw URL.
 *   • Tooltip carries the full URL plus, when available, the
 *     classifier `reason` for transparency.
 *
 * No new wide column — caller is expected to inline this beside
 * the entity name. Matches the existing IBM-Plex / muted-secondary
 * typographic palette.
 */
export default function ContactPathLink({
  targetUrl,
  bestEvidenceUrl,
  reason,
  testIdPrefix,
}) {
  const url = (targetUrl || bestEvidenceUrl || '').trim();
  if (!url) return null;
  const isContact = !!targetUrl;
  const label = isContact ? 'Contact path' : 'Evidence source';

  let host = '';
  try {
    const u = new URL(url.startsWith('http') ? url : `https://${url}`);
    host = u.hostname.replace(/^www\./i, '');
  } catch {
    host = url;
  }

  const fullTitle = reason
    ? `${label}: ${url}\n${reason}`
    : `${label}: ${url}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={fullTitle}
      aria-label={`${label} — ${host}`}
      data-testid={testIdPrefix ? `${testIdPrefix}-link` : undefined}
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        marginLeft: 8,
        fontSize: 11,
        fontFamily: "'IBM Plex Mono', monospace",
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
      }}
    >
      <ExternalLink size={11} aria-hidden="true" />
      <span data-testid={testIdPrefix ? `${testIdPrefix}-host` : undefined}>
        {host}
      </span>
    </a>
  );
}
